import { zodResolver } from "@hookform/resolvers/zod";
import { createUserWithEmailAndPassword, getAuth } from "firebase/auth";
import { collection, doc, getDocs, getFirestore, setDoc } from "firebase/firestore";
import { useEffect, useState } from 'react';
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { app } from "../firebase";
import { useToast } from "../hooks/use-toast";
import { Clipboard, Check, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Validation schema
const createUserSchema = z.object({
  firstName: z.string().min(1, { message: "First Name is required." }),
  lastName: z.string().min(1, { message: "Last Name is required." }),
  email: z.string().email({ message: "Valid Email is required." }),
  phone: z.string().min(10, { message: "Valid Phone number is required." }),
  role: z.string().min(1, { message: "Role is required." }),
  password: z.string().min(6, { message: "Password must be at least 6 characters long." }),
  sendEmail: z.boolean().optional(),
});

const CreateUser = ({ user = null }) => {
  const { toast } = useToast();
  const [roles, setRoles] = useState([]);
  const [isMasterAdmin, setIsMasterAdmin] = useState(user?.role === "MASTERADMIN");
  const [loading, setLoading] = useState(false);

  // Form setup using react-hook-form
  const form = useForm({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      role: "",
      password: "",
      sendEmail: true,
    },
  });

  const { handleSubmit, control, setValue, setError, formState: { errors } } = form;

  const db = getFirestore(app);
  const auth = getAuth(app);
  const navigate = useNavigate();

  // Fetch roles from the Firebase roles table
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const rolesCollection = collection(db, "roles");
        const snapshot = await getDocs(rolesCollection);
        const fetchedRoles = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        const rolesArray = fetchedRoles
          .filter((role:any) => isMasterAdmin || role.role !== "MASTERADMIN")
          .map((role:any) => role.role)
          .sort();
        setRoles(rolesArray);
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };

    fetchRoles();
  }, [db, isMasterAdmin]);

  const generateTemporaryPassword = (email) => {
  const baseEmailPart = email.substring(0, 5).toLowerCase();
  const staticPart = "@#$%^&321";
  const requiredLength = 12;

  // The total length of the base parts
  const currentLength = baseEmailPart.length + staticPart.length;

  if (currentLength >= requiredLength) {
    // If the combined parts are already 12 or more characters,
    // we just return a sliced version to ensure it's not longer.
    return (baseEmailPart + staticPart).substring(0, requiredLength);
  } else {
    // If it's shorter, we pad it with random alphanumeric characters.
    const randomCharsNeeded = requiredLength - currentLength;
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let randomPart = "";
    for (let i = 0; i < randomCharsNeeded; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      randomPart += characters.charAt(randomIndex);
    }
    return baseEmailPart + staticPart + randomPart;
  }
};

  // Handle form submission
  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, data.email, data.password);
      const user = userCredential.user;
      const userData = {
        firstName: data?.firstName,
        lastName: data?.lastName,
        fullName:`${data?.firstName} ${data?.lastName}`,
        emailId: data.email,
        phone: data.phone,
        role: data.role,
        uid: user?.uid,
        createdAt: new Date(),
        status:'active'
      };
      const newUserRef = doc(collection(db, "BDUsers"));
      await setDoc(newUserRef, { id: newUserRef.id, ...userData });
      toast({
        title: "User Created",
        description: "User has been successfully created.",
      });
      form.reset();
      navigate("/admin/manage-access");
    } catch (error) {
      console.error("Error creating user:", error.message);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handling email change to generate password
  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const email = event.target.value;
    setValue("email", email);
    // Generate temporary password based on the email
    if (email) {
      const tempPassword = generateTemporaryPassword(email);
      setValue("password", tempPassword);
    }
  };

  // Copy password to clipboard with animation/feedback
  const [copied, setCopied] = useState(false);
  const copyPasswordToClipboard = async () => {
    const password = form.getValues("password");
    await navigator.clipboard.writeText(password);
    setCopied(true);
    toast({
      title: "Password Copied",
      description: "The temporary password has been copied to your clipboard.",
    });
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div className="container py-6 space-y-6">
      <div className="flex justify-center items-center w-full mb-2">
        <h1 className="text-2xl font-bold text-center">Create New User</h1>
      </div>
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>Add User Account</CardTitle>
          <CardDescription>Create a new staff or admin user account</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  {...form.register("firstName")}
                  placeholder="First name"
                  className={errors.firstName ? "border-red-500" : ""}
                />
                {errors.firstName && <p className="text-red-500 text-sm">{errors.firstName.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  {...form.register("lastName")}
                  placeholder="Last name"
                  className={errors.lastName ? "border-red-500" : ""}
                />
                {errors.lastName && <p className="text-red-500 text-sm">{errors.lastName.message}</p>}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                {...form.register("email", { onChange: handleEmailChange })}
                placeholder="Email address"
                className={errors.email ? "border-red-500" : ""}
              />
              {errors.email && <p className="text-red-500 text-sm">{errors.email.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                {...form.register("phone")}
                placeholder="Phone number"
                className={errors.phone ? "border-red-500" : ""}
              />
              {errors.phone && <p className="text-red-500 text-sm">{errors.phone.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                onValueChange={(value) => {
                  setValue("role", value);
                  if (errors.role) {
                    setError("role", { message: "" });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role, index) => (
                    <SelectItem key={index} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.role && <p className="text-red-500 text-sm">{errors.role.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Temporary Password</Label>
              <div className="flex items-center space-x-2">
                <Input
                  id="password"
                  type="password"
                  {...form.register("password")}
                  placeholder="Temporary password"
                  className={errors.password ? "border-red-500" : ""}
                />
                <Button
                  type="button"
                  onClick={copyPasswordToClipboard}
                  className="bg-[#0071bd] hover:bg-[#005fa3] border-[#0071bd] text-white flex items-center justify-center transition-all duration-200"
                  aria-label="Copy password"
                >
                  {copied ? <Check className="text-green-400 animate-bounce" /> : <Clipboard />}
                </Button>
              </div>
              {errors.password && <p className="text-red-500 text-sm">{errors.password.message}</p>}
            </div>
            <div className="flex items-start space-x-2 pt-2">
              <Checkbox
                id="send-email"
                {...form.register("sendEmail")}
                checked={form.getValues("sendEmail")}
              />
              <div className="grid gap-1.5 leading-none">
                <p className="text-sm text-gray-500">
                  User will receive login details in their email
                </p>
              </div>
            </div>
            <CardFooter className="flex justify-between py-8 px-0">
              <Button variant="outline" type="button" onClick={() => navigate('/admin/manage-access')} className="border-[#0071bd] text-[#0071bd] hover:bg-[#e6f1fa]">
                Cancel
              </Button>
              <Button type="submit" className="bg-[#0071bd] hover:bg-[#005fa3] border-[#0071bd] text-white flex items-center justify-center" disabled={loading}>
                {loading ? <Loader2 className="animate-spin mr-2" size={20} /> : null}
                {loading ? "Creating..." : "Create User"}
              </Button>
            </CardFooter>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateUser;
